import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReactComponent as RadioOn } from '../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../assets/images/svg/radio-off.svg';
import { formatAddress } from '../../util/helpers/addressHelper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import {
  uButtonClear,
  uFlexColumn,
  uFullWidth,
  uOutlineNone,
} from '../../assets/styles/utility';
import { pxToRem, pxToRemMd } from '../../assets/styles/helper';
import Paragraph from '../Paragraph/Paragraph';
import { ReactComponent as AlertTriangle } from '../../assets/images/svg/alert-triangle.svg';

export const DeliveryAddressContainer = styled.li`
  display: flex;
  flex-direction: column;
  border: 1px solid transparent;
  box-shadow: ${variables.shadow.boxShadow};
  border-radius: ${variables.borderRadius.borderRadius};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  ${(props) => props.isSelected && `border-color: ${themeColors.colorFocus}`};
`;

export const DeliveryAddressButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: flex;
  align-items: center;
  cursor: pointer;
  ${uFullWidth};
  padding: ${pxToRem(8)} ${pxToRem(16)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

export const DeliveryAddressIndicator = styled.span`
  width: ${pxToRem(16)};
  margin-right: ${pxToRem(18)};
`;

export const DeliveryAddressValue = styled.p`
  ${uFlexColumn};
  align-items: flex-start;
  font-size: ${pxToRem(14)};
  line-height: 1.75;
  text-align: left;
`;

const WarningNote = styled(Paragraph)`
  display: flex;
  align-items: center;
  color: ${themeColors.colorRed};
  font-size: ${pxToRemMd(12)};
  margin-top: ${pxToRem(4)};

  svg {
    color: ${themeColors.colorRed};
    margin-left: 0;
    margin-top: 0;
    margin-right: ${pxToRem(4)};
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }
`;

const DeliveryAddress = ({ address, isSelected, onClick }) => {
  const { t } = useTranslation();
  const isDisabled = address?.IsPoBox;

  return (
    <>
      <DeliveryAddressContainer
        isSelected={isSelected}
        key={address?.AddressUid}
        disabled={isDisabled}
      >
        <DeliveryAddressButton
          type="button"
          onClick={() => !isDisabled && onClick(address)}
          disabled={isDisabled}
        >
          <DeliveryAddressIndicator>
            {isSelected ? <RadioOn /> : <RadioOff />}
          </DeliveryAddressIndicator>
          <DeliveryAddressValue>
            {formatAddress(address)}
            {isDisabled && (
              <WarningNote
                fontSize={16}
                color={themeColors.colorTextSecondary}
                bold
              >
                <AlertTriangle />
                {t('buyWizard.buyForDelivery.chooseDeliveryAddress.poBox')}
              </WarningNote>
            )}
          </DeliveryAddressValue>
        </DeliveryAddressButton>
      </DeliveryAddressContainer>
    </>
  );
};

DeliveryAddress.propTypes = {
  address: PropTypes.shape({
    AddressLine: PropTypes.string,
    City: PropTypes.string,
    StateRegion: PropTypes.string,
    PostalCode: PropTypes.string,
    AddressUid: PropTypes.string,
    AddressLine2: PropTypes.string,
    CountryIso3Code: PropTypes.string,
    IsPoBox: PropTypes.bool,
  }),
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
};

export default DeliveryAddress;
